// HMA Sniper Breakout System Engine (Frontend & Replay)
// Price Action Swing Reversal + Hull Moving Average (Zero-Lag)
// Logic:
// 1. BUY: Price below HMA tracks Lowest Base Candle (Lowest Low/High). When a candle closes ABOVE HMA & breaks ABOVE Lowest High -> BUY Market / Limit, SL at Lowest Low, TP by RR.
// 2. SELL: Price above HMA tracks Highest Base Candle (Highest High/Low). When a candle closes BELOW HMA & breaks BELOW Highest Low -> SELL Market / Limit, SL at Highest High, TP by RR.

export function getPipMultiplier(price) {
  if (price < 5.0) return 10000.0;     // EURUSD, GBPUSD (0.0001 = 1 pip)
  if (price < 500.0) return 100.0;     // USDJPY (0.01 = 1 pip)
  if (price < 15000.0) return 10.0;    // Gold XAUUSD ($1.00 = 10 pips, 0.1 = 1 pip)
  return 1.0;                          // BTCUSD ($1 = 1 pip)
}

export function getPriceDecimals(price) {
  if (price < 5.0) return 5;
  if (price < 500.0) return 3;
  if (price < 15000.0) return 2;
  return 2;
}

export function isInSession(t, mode = "ALL", startStr = "06:00", endStr = "19:30") {
  const m = String(mode || "ALL").toUpperCase();
  if (m === "ALL") return true;
  try {
    const vnTs = t + 7 * 3600;
    const date = new Date(vnTs * 1000);
    const curMin = date.getUTCHours() * 60 + date.getUTCMinutes();

    if (m === "ASIA") {
      return curMin >= 360 && curMin <= 840; // 06:00 - 14:00 VN
    } else if (m === "LONDON") {
      return curMin >= 840 && curMin <= 1260; // 14:00 - 21:00 VN
    } else if (m === "NY") {
      return curMin >= 1170 || curMin <= 210; // 19:30 - 03:30 VN
    } else if (m === "ASIA_LONDON") {
      return curMin >= 360 && curMin <= 1170; // 06:00 - 19:30 VN
    } else if (m === "LONDON_NY") {
      return curMin >= 840 || curMin <= 210; // 14:00 - 03:30 VN
    } else if (m === "CUSTOM") {
      const [sH, sM] = (startStr || "06:00").split(":").map(Number);
      const [eH, eM] = (endStr || "19:30").split(":").map(Number);
      const sMin = sH * 60 + sM;
      const eMin = eH * 60 + eM;
      if (sMin <= eMin) {
        return curMin >= sMin && curMin <= eMin;
      } else {
        return curMin >= sMin || curMin <= eMin;
      }
    }
    return true;
  } catch (e) {
    return true;
  }
}

export function calcWMA(data, p) {
  const n = data.length;
  const res = new Array(n);
  const weightSum = (p * (p + 1)) / 2;
  for (let i = 0; i < n; i++) {
    if (i < p - 1) {
      let sum = 0;
      let subWeight = 0;
      for (let j = 0; j <= i; j++) {
        const w = j + 1;
        sum += data[j] * w;
        subWeight += w;
      }
      res[i] = subWeight > 0 ? sum / subWeight : data[i];
    } else {
      let sum = 0;
      for (let j = 0; j < p; j++) {
        sum += data[i - p + 1 + j] * (j + 1);
      }
      res[i] = sum / weightSum;
    }
  }
  return res;
}

export function calcHMASeries(closes, period = 30) {
  const n = closes.length;
  if (n === 0) return [];
  const p = Math.max(2, period);
  const halfP = Math.max(1, Math.floor(p / 2));
  const sqrtP = Math.max(1, Math.floor(Math.sqrt(p)));

  const wmaHalf = calcWMA(closes, halfP);
  const wmaFull = calcWMA(closes, p);

  const raw = new Array(n);
  for (let i = 0; i < n; i++) {
    raw[i] = 2.0 * wmaHalf[i] - wmaFull[i];
  }

  return calcWMA(raw, sqrtP);
}

export function calcAdaptiveHMASeries(closes, minPeriod = 21, maxPeriod = 35, erWindow = 20) {
  const n = closes.length;
  if (n === 0) return [];
  const pMin = Math.max(2, Math.min(minPeriod, maxPeriod));
  const pMax = Math.max(pMin, Math.max(minPeriod, maxPeriod));
  if (pMin === pMax) return calcHMASeries(closes, pMin);

  const hmaFast = calcHMASeries(closes, pMin);
  const hmaSlow = calcHMASeries(closes, pMax);

  const w = Math.max(5, Math.min(erWindow, n - 1));
  const diffs = new Array(n).fill(0);
  for (let i = 1; i < n; i++) {
    diffs[i] = Math.abs(closes[i] - closes[i - 1]);
  }
  const cumsum = new Array(n).fill(0);
  let acc = 0;
  for (let i = 0; i < n; i++) {
    acc += diffs[i];
    cumsum[i] = acc;
  }

  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    if (i < w) {
      out[i] = hmaFast[i];
    } else {
      const direction = Math.abs(closes[i] - closes[i - w]);
      const volatility = Math.max(cumsum[i] - cumsum[i - w], 1e-6);
      const er = Math.min(1.0, Math.max(0.0, direction / volatility));
      out[i] = er * hmaFast[i] + (1.0 - er) * hmaSlow[i];
    }
  }
  return out;
}

export function calcEMA(data, period = 200) {
  const n = data.length;
  if (n === 0) return [];
  const k = 2 / (period + 1);
  const res = new Array(n);
  let prevEma = data[0] || 0;
  for (let i = 0; i < n; i++) {
    if (i === 0) {
      prevEma = data[0];
    } else {
      prevEma = data[i] * k + prevEma * (1 - k);
    }
    res[i] = prevEma;
  }
  return res;
}

export function calcATRSeries(highs, lows, closes, period = 14) {
  const n = closes.length;
  if (n === 0) return [];
  const tr = new Array(n);
  tr[0] = highs[0] - lows[0];
  for (let i = 1; i < n; i++) {
    const hl = highs[i] - lows[i];
    const hc = Math.abs(highs[i] - closes[i - 1]);
    const lc = Math.abs(lows[i] - closes[i - 1]);
    tr[i] = Math.max(hl, hc, lc);
  }
  const atr = new Array(n);
  const p = Math.max(1, period);
  if (n < p) return tr;
  let sumP = 0;
  for (let i = 0; i < p; i++) sumP += tr[i];
  const firstAtr = sumP / p;
  for (let i = 0; i < p; i++) atr[i] = firstAtr;
  let currAtr = firstAtr;
  for (let i = p; i < n; i++) {
    currAtr = (currAtr * (p - 1) + tr[i]) / p;
    atr[i] = currAtr;
  }
  return atr;
}

export function calcEMASeries(arr, period = 200) {
  const n = arr.length;
  if (n === 0) return [];
  const k = 2.0 / (period + 1.0);
  const out = new Array(n);
  let prev = arr[0];
  out[0] = prev;
  for (let i = 1; i < n; i++) {
    prev = arr[i] * k + prev * (1.0 - k);
    out[i] = prev;
  }
  return out;
}

export function resampleBarsToM5(bars) {
  if (!bars || bars.length === 0) return [];
  const periodSec = 300;
  const result = [];
  let current = null;
  let currentBucket = null;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const bucket = Math.floor(b.time / periodSec) * periodSec;
    if (bucket !== currentBucket) {
      if (current) result.push(current);
      currentBucket = bucket;
      current = {
        time: bucket,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close
      };
    } else {
      current.high = Math.max(current.high, b.high);
      current.low = Math.min(current.low, b.low);
      current.close = b.close;
    }
  }
  if (current) result.push(current);
  return result;
}

export function calculateHMASystem(bars, params = {}) {
  const period = Number(params.hmaPeriod ?? params.hma_period ?? params.period ?? 30);
  const lookback = Number(params.hmaLookbackBars ?? params.hma_lookback_bars ?? params.lookbackBars ?? params.lookback ?? params.scanLookback ?? 100);
  const maxBarsFromExtreme = Number(params.hmaMaxBarsFromExtreme ?? params.hma_max_bars_from_extreme ?? params.maxBarsFromExtreme ?? 15);
  const rawMinBodyPct = Number(params.hmaMinBodyPct ?? params.hma_min_body_pct ?? params.minBodyPct ?? params.min_body_pct ?? 0.4);
  const minBodyPct = rawMinBodyPct > 1.0 ? rawMinBodyPct / 100.0 : rawMinBodyPct;
  const hmaSlopeFilter = Boolean(params.hmaSlopeFilter !== undefined ? params.hmaSlopeFilter : (params.hma_slope_filter !== undefined ? params.hma_slope_filter : true));
  const maxHmaSlope = Number(params.hmaMaxSlope ?? params.hma_max_slope ?? params.maxHmaSlope ?? 3.0);
  const r1 = Number(params.hmaR1 ?? params.hma_r1 ?? params.r1 ?? 1.8);
  const r2 = Number(params.hmaR2 ?? params.hma_r2 ?? params.r2 ?? 2.0);
  
  // SL Buffer Parameters (Dynamic ATR mode with bounds 5-30 pips)
  const slBufferMode = String(params.hmaSlBufferMode ?? params.slBufferMode ?? params.hma_sl_buffer_mode ?? "ATR").toUpperCase();
  const slBufferMinPips = Number(params.hmaSlBufferMinPips ?? params.slBufferMinPips ?? params.hma_sl_buffer_min_pips ?? 5.0);
  const slBufferMaxPips = Number(params.hmaSlBufferMaxPips ?? params.slBufferMaxPips ?? params.hma_sl_buffer_max_pips ?? 30.0);
  const slAtrMult = Number(params.hmaSlAtrMult ?? params.slAtrMult ?? params.hma_sl_atr_mult ?? 1.0);
  const slBufferPips = Number(params.hmaSlBufferPips ?? params.hma_sl_buffer_pips ?? params.slBufferPips ?? 20.0);
  const slAtrPeriod = Math.max(5, Number(params.slAtrPeriod ?? params.hmaSlAtrPeriod ?? params.hma_sl_atr_period ?? 20));
  const minRiskPips = Number(params.hmaMinRiskPips ?? params.hma_min_risk_pips ?? params.minRiskPips ?? 3.0);
  const maxRiskPips = Number(params.hmaMaxRiskPips ?? params.hma_max_risk_pips ?? params.maxRiskPips ?? 200.0);
  const cooldownBars = Number(params.hmaCooldownBars ?? params.hma_cooldown_bars ?? params.cooldownBars ?? 5);
  const allowFloatingBreakout = Boolean(params.allowFloatingBreakout ?? params.allow_floating_breakout ?? params.hma_allow_floating_breakout ?? false);
  const candleStrengthFilter = Boolean(params.candleStrengthFilter ?? params.candle_strength_filter ?? params.hma_candle_strength_filter ?? false);
  const rawBodyRatio = Number(params.minCandleBodyRatio ?? params.min_candle_body_ratio ?? params.hma_min_candle_body_ratio ?? 0.35);
  const minCandleBodyRatio = rawBodyRatio > 1.0 ? rawBodyRatio / 100.0 : rawBodyRatio;
  const rawWickRatio = Number(params.maxRejectionWickRatio ?? params.max_rejection_wick_ratio ?? params.hma_max_rejection_wick_ratio ?? 0.40);
  const maxRejectionWickRatio = rawWickRatio > 1.0 ? rawWickRatio / 100.0 : rawWickRatio;

  // Dynamic Limit Order settings
  const dynamicLimitEnable = Boolean(params.dynamicLimitEnable ?? params.dynamic_limit_enable ?? params.hmaDynamicLimitEnable ?? params.hma_dynamic_limit_enable ?? false);
  const limitThresholdPips = Number(params.limitThresholdPips ?? params.limit_threshold_pips ?? params.hmaLimitThresholdPips ?? params.hma_limit_threshold_pips ?? 80.0);
  const limitPullbackPct = Number(params.limitPullbackPct ?? params.limit_pullback_pct ?? params.hmaLimitPullbackPct ?? params.hma_limit_pullback_pct ?? 50.0);
  const limitCancelBars = Number(params.limitCancelBars ?? params.limit_cancel_bars ?? params.hmaLimitCancelBars ?? params.hma_limit_cancel_bars ?? 10);

  // Dynamic Break-Even settings (e.g. Move SL to BE + 5 pips when price reaches 75% of TP)
  const dynamicBeEnable = Boolean(params.dynamicBeEnable ?? params.dynamic_be_enable ?? params.hmaDynamicBeEnable ?? params.hma_dynamic_be_enable ?? false);
  const rawBeTrig = Number(params.beTriggerPct ?? params.be_trigger_pct ?? params.hmaBeTriggerPct ?? params.hma_be_trigger_pct ?? 75.0);
  const rawBeLock = Number(params.beLockPips ?? params.be_lock_pips ?? params.hmaBeLockPips ?? params.hma_be_lock_pips ?? 5.0);
  const beLockPips = Math.max(rawBeLock, 0.0);

  // Near-TP Reversal Exit (Early exit when price reaches e.g. 85-90% TP and subsequent candle closes breaking opposite)
  const nearTpCutEnable = Boolean(params.nearTpCutEnable ?? params.near_tp_cut_enable ?? params.hmaNearTpCutEnable ?? params.hma_near_tp_cut_enable ?? false);
  const rawNearTpThresh = Number(params.nearTpThresholdPct ?? params.near_tp_threshold_pct ?? params.hmaNearTpThresholdPct ?? params.hma_near_tp_threshold_pct ?? 85.0);
  const nearTpThresholdPct = rawNearTpThresh > 1.0 ? rawNearTpThresh / 100.0 : rawNearTpThresh;

  // Anti-Cascade Dump & Steep Slope Protections
  const hmaCurlFilter = Boolean(params.hmaCurlFilter !== undefined ? params.hmaCurlFilter : (params.hma_curl_filter !== undefined ? params.hma_curl_filter : (params.hmaCurlEnable !== undefined ? params.hmaCurlEnable : true)));
  const cascadeFilter = Boolean(params.cascadeFilter !== undefined ? params.cascadeFilter : (params.cascade_filter !== undefined ? params.cascade_filter : (params.hmaCascadeFilter !== undefined ? params.hmaCascadeFilter : false)));
  const rawBearRatio = Number(params.bearRatioThresh ?? params.bear_ratio_thresh ?? params.hmaBearRatioThresh ?? 70.0);
  const bearRatioThresh = rawBearRatio > 1.0 ? rawBearRatio / 100.0 : rawBearRatio;

  // Trading Hours / Session Filter
  const sessionFilterEnable = Boolean(params.sessionFilterEnable ?? params.hmaSessionFilterEnable ?? params.hma_session_filter_enable ?? false);
  const sessionMode = String(params.sessionMode ?? params.hmaSessionMode ?? params.hma_session_mode ?? "ALL").toUpperCase();
  const sessionStart = String(params.sessionStart ?? params.hmaSessionStart ?? params.hma_session_start ?? "06:00");
  const sessionEnd = String(params.sessionEnd ?? params.hmaSessionEnd ?? params.hma_session_end ?? "19:30");

  // Close on Opposite Signal (Cut old trade when reverse signal appears)
  const closeOnOpposite = Boolean(params.closeOnOpposite !== undefined ? params.closeOnOpposite : (params.close_on_opposite !== undefined ? params.close_on_opposite : (params.hmaCloseOnOpposite !== undefined ? params.hmaCloseOnOpposite : (params.hma_close_on_opposite !== undefined ? params.hma_close_on_opposite : true))));

  // Sideway M5 EMA 200 Range Filter (Chỉ áp dụng cho nến M1, M5 trở lên auto lọc KHÔNG chạy)
  const ema200SwFilterEnable = Boolean(params.ema200SwFilterEnable ?? params.ema200_sw_filter_enable ?? params.hmaEma200SwFilterEnable ?? params.hma_ema200_sw_filter_enable ?? false);
  const rawSwDist = Number(params.ema200SwMaxDistPips ?? params.ema200_sw_max_dist_pips ?? params.hmaEma200SwMaxDistPips ?? params.hma_ema200_sw_max_dist_pips ?? 300.0);
  const ema200SwMaxDistPips = Math.max(10.0, rawSwDist);

  const tfStr = String(params.timeframe ?? params.currentTimeframe ?? params.tf ?? "").toUpperCase().trim();
  const isSubM5 = (bars && bars.length >= 2 && (Number(bars[1].time) - Number(bars[0].time)) < 240);
  const isM1 = tfStr === "M1" ? true : (tfStr ? false : isSubM5);
  const activeEma200SwFilter = Boolean(ema200SwFilterEnable && isM1);

  // Volatility-Adaptive HMA Period
  const adaptivePeriodEnable = Boolean(params.adaptivePeriodEnable ?? params.adaptive_period_enable ?? params.hmaAdaptivePeriodEnable ?? params.hma_adaptive_period_enable ?? false);
  const adaptiveMinPeriod = Math.max(2, Number(params.adaptiveMinPeriod ?? params.adaptive_min_period ?? params.hmaAdaptiveMinPeriod ?? params.hma_adaptive_min_period ?? 21));
  const adaptiveMaxPeriod = Math.max(adaptiveMinPeriod, Number(params.adaptiveMaxPeriod ?? params.adaptive_max_period ?? params.hmaAdaptiveMaxPeriod ?? params.hma_adaptive_max_period ?? 35));

  const emptyResult = {
    hma: [],
    signals: [],
    recent_signals: [],
    all_signals: [],
    latest_signal: null,
    latestSignal: null,
    chart_markers: [],
    chartMarkers: [],
    visual_lines: [],
    visualLines: [],
    base_boxes: [],
    baseBoxes: [],
    market_bias: "NEUTRAL",
    streaks: { max_win_streak: 0, max_loss_streak: 0 },
    backtest: {
      total_trades: 0,
      win_rate: 0,
      wins: 0,
      losses: 0,
      net_pnl: 0,
      profit_factor: 1.0,
      avg_rr: `1:${r1}`,
    },
    backtest_windows: {},
  };

  if (!bars || bars.length < period + 5) {
    return emptyResult;
  }

  const n = bars.length;
  const times = bars.map((b) => Number(b.time));
  const opens = bars.map((b) => Number(b.open));
  const highs = bars.map((b) => Number(b.high));
  const lows = bars.map((b) => Number(b.low));
  const closes = bars.map((b) => Number(b.close));

  const hmaValues = adaptivePeriodEnable
    ? calcAdaptiveHMASeries(closes, adaptiveMinPeriod, adaptiveMaxPeriod)
    : calcHMASeries(closes, period);
  const hmaLine = [];

  for (let i = 0; i < n; i++) {
    const val = Number(hmaValues[i].toFixed(6));
    const isBull = i === 0 || hmaValues[i] >= hmaValues[i - 1];
    hmaLine.push({ time: times[i], value: val, isBull });
  }

  const atrValues = calcATRSeries(highs, lows, closes, slAtrPeriod);
  const pipMultiplier = getPipMultiplier(closes[closes.length - 1]);
  const decimals = getPriceDecimals(closes[closes.length - 1]);
  const factor = Math.pow(10, decimals);
  const beTriggerPct = rawBeTrig > 1.0 ? rawBeTrig / 100.0 : rawBeTrig;
  const beLockPrice = beLockPips / pipMultiplier;

  let m5Ema200Map = null;
  if (activeEma200SwFilter && isSubM5) {
    const barsM5 = resampleBarsToM5(bars);
    if (barsM5.length >= 10) {
      const m5Closes = barsM5.map(b => Number(b.close));
      const m5Ema = calcEMASeries(m5Closes, 200);
      m5Ema200Map = new Map();
      for (let k = 0; k < barsM5.length; k++) {
        m5Ema200Map.set(barsM5[k].time, m5Ema[k]);
      }
    }
  }

  const usedBottomTimes = new Set();
  const usedPeakTimes = new Set();
  const rawTrades = [];
  const baseBoxes = [];

  const scanWindow = Math.max(10, Math.min(300, lookback));
  const startIdx = Math.max(period + 5, Math.min(scanWindow + 2, Math.floor(n / 4)));
  let lastSignalIdx = -999999;

  for (let i = startIdx; i < n; i++) {
    if (i - lastSignalIdx < cooldownBars) continue;

    const t = times[i];

    // Session Filter Check on Breakout Candle
    if (sessionFilterEnable && !isInSession(t, sessionMode, sessionStart, sessionEnd)) {
      continue;
    }

    const h = highs[i];
    const l = lows[i];
    const o = opens[i];
    const prevC = closes[i - 1];
    const c = closes[i];

    // Sideway Range Filter: Disallow entry if price is beyond N pips of M5 EMA 200 (CHỈ chạy trên M1, M5 auto không chạy)
    let distToM5Ema = null;
    if (activeEma200SwFilter && m5Ema200Map) {
      const bucket = Math.floor(t / 300) * 300;
      const emaVal = m5Ema200Map.get(bucket) ?? closes[i];
      distToM5Ema = Math.abs(c - emaVal) * pipMultiplier;
      if (distToM5Ema > ema200SwMaxDistPips) {
        continue; // Skip both BUY and SELL: runaway trend or waterfall, not in sideway envelope
      }
    }
    const hmaVal = hmaValues[i];
    const hmaSlope5 = hmaValues[i] - hmaValues[i - 5];

    let currentBufPips = slBufferPips;
    if (slBufferMode === "ATR" && atrValues.length > i) {
      const candleAtrPips = (atrValues[i] || (h - l)) * pipMultiplier;
      currentBufPips = Math.max(slBufferMinPips, Math.min(slBufferMaxPips, candleAtrPips * slAtrMult));
    }
    const slBufferPrice = currentBufPips / pipMultiplier;

    // ================= 1. CHECK BUY BREAKOUT =================
    // Bullish candle closing above HMA
    if (c > o && c > hmaVal) {
      const candleRange = Math.max(0.0001, h - l);
      const bodyRatio = (c - o) / candleRange;
      const upperWickRatio = (h - c) / candleRange;

      // Strength filter: ensure solid body & prevent long rejection upper wick
      if (candleStrengthFilter && (bodyRatio < minCandleBodyRatio || upperWickRatio > maxRejectionWickRatio)) {
        continue;
      }

      const isCrossing = Math.min(o, l) <= hmaVal;
      if (isCrossing || allowFloatingBreakout) {
        if (!hmaSlopeFilter || hmaSlope5 >= -maxHmaSlope) {
          // Anti-Cascade Dump Protection (Block BUY catching falling knife when 80%+ recent candles are bearish)
          if (cascadeFilter) {
            const checkStart = Math.max(0, i - 10);
            let bearCount = 0;
            for (let k = checkStart; k < i; k++) {
              if (closes[k] < opens[k]) bearCount++;
            }
            const bearRatio = bearCount / Math.max(1, i - checkStart);
            if (bearRatio >= bearRatioThresh) {
              continue;
            }
          }

          const bodyTop = c;
          const bodyBot = o;
          const bodyLen = Math.max(0.0001, bodyTop - bodyBot);
          const abovePct = Math.min(o, l) > hmaVal ? 1.0 : Math.max(0.0, bodyTop - Math.max(bodyBot, hmaVal)) / bodyLen;

          if (abovePct >= minBodyPct) {
            // Scan lookback window for candidate candles completely below HMA
            const wStart = Math.max(0, i - scanWindow);
            const candidateIndices = [];
            for (let k = wStart; k < i; k++) {
              if (highs[k] < hmaValues[k]) {
                candidateIndices.push(k);
              }
            }

            if (candidateIndices.length > 0) {
              let minIdx = candidateIndices[0];
              for (let idx of candidateIndices) {
                if (lows[idx] < lows[minIdx]) {
                  minIdx = idx;
                }
              }

              // 1. Must be within recent bars (1 <= i - minIdx <= maxBarsFromExtreme)
              // 2. Must not have been traded already
              if (i - minIdx >= 1 && i - minIdx <= maxBarsFromExtreme) {
                const minH = highs[minIdx];
                const minL = lows[minIdx];
                const minT = times[minIdx];

                if (!usedBottomTimes.has(minT)) {
                  // Standard clean breakout above base low's high
                  if (prevC <= minH && c >= minH) {
                    const marketEntry = Math.round(c * factor) / factor;
                    const slPrice = Math.round((minL - slBufferPrice) * factor) / factor;
                    const marketRisk = marketEntry - slPrice;
                    const marketRiskPips = Math.round(marketRisk * pipMultiplier * 10) / 10;

                    if (marketRiskPips >= minRiskPips && marketRiskPips <= maxRiskPips) {
                      let entryMode = "MARKET";
                      let entryPrice = marketEntry;

                      // Dynamic Limit on large breakout candle (Pullback 50% of entire SL Zone)
                      if (dynamicLimitEnable && marketRiskPips > limitThresholdPips) {
                        entryMode = "LIMIT";
                        const pct = limitPullbackPct / 100.0;
                        entryPrice = Math.round((c - pct * (c - slPrice)) * factor) / factor;
                      }

                      const risk = entryPrice - slPrice;
                      const riskPips = Math.round(risk * pipMultiplier * 10) / 10;
                      const tp1Price = Math.round((marketEntry + r1 * marketRisk) * factor) / factor;
                      const tp2Price = Math.round((marketEntry + r2 * marketRisk) * factor) / factor;

                      const baseBox = {
                        x1: minT,
                        x2: t,
                        y1: minL,
                        y2: minH,
                        type: "BUY_BASE",
                        label: "Base Low",
                      };
                      baseBoxes.push(baseBox);

                      const reasons = [
                        `Bắt sóng hồi từ đáy thấp nhất ${scanWindow} nến (${i - minIdx} nến trước)`,
                        `Nến tăng đóng cửa có ${Math.round(abovePct * 100)}% thân nằm trên HMA`,
                        `Phá vỡ đỉnh cây nến đáy thấp nhất (${minH})`,
                        `Cắt lỗ dưới đáy râu nến (${minL})`
                      ];

                      if (entryMode === "LIMIT") {
                        reasons.unshift(`SL nến lớn (${marketRiskPips}p > ${limitThresholdPips}p): Chờ Limit hồi ${Math.round(limitPullbackPct)}% Zone tại ${entryPrice} (Hủy sau ${limitCancelBars} nến)`);
                      }

                      const sig = {
                        id: `hma_${t}_BUY`,
                        index: i,
                        time: t,
                        action: "BUY",
                        type: "HMA BREAKOUT BUY",
                        entry_mode: entryMode,
                        market_entry: marketEntry,
                        price: entryPrice,
                        entry: entryPrice,
                        order_price: entryPrice,
                        sl: slPrice,
                        tp1: tp1Price,
                        tp2: tp2Price,
                        tp: tp1Price,
                        r1,
                        r2,
                        rr_ratio: `1:${r1}`,
                        confidence: 92,
                        reasons,
                        riskPips,
                        risk_pips: riskPips,
                        market_risk_pips: marketRiskPips,
                        limit_cancel_bars: limitCancelBars,
                        limit_pullback_pct: limitPullbackPct,
                        baseBox,
                        base_box: baseBox,
                        status: "OPEN",
                        exitTime: null,
                        exitPrice: null,
                        pnl_diff: 0.0,
                        pnlPips: 0,
                        win: false,
                      };

                      rawTrades.push(sig);
                      usedBottomTimes.add(minT);
                      lastSignalIdx = i;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // ================= 2. CHECK SELL BREAKDOWN =================
    // Bearish candle closing below HMA
    if (c < o && c < hmaVal) {
      const candleRange = Math.max(0.0001, h - l);
      const bodyRatio = (o - c) / candleRange;
      const lowerWickRatio = (c - l) / candleRange;

      // Strength filter: ensure solid body & prevent long rejection lower wick
      if (candleStrengthFilter && (bodyRatio < minCandleBodyRatio || lowerWickRatio > maxRejectionWickRatio)) {
        continue;
      }

      const isCrossing = Math.max(o, h) >= hmaVal;
      if (isCrossing || allowFloatingBreakout) {
        if (!hmaSlopeFilter || hmaSlope5 <= maxHmaSlope) {
          // Anti-Cascade Pump Protection (Block SELL shorting into rocket pump when 80%+ recent candles are bullish)
          if (cascadeFilter) {
            const checkStart = Math.max(0, i - 10);
            let bullCount = 0;
            for (let k = checkStart; k < i; k++) {
              if (closes[k] > opens[k]) bullCount++;
            }
            const bullRatio = bullCount / Math.max(1, i - checkStart);
            if (bullRatio >= bearRatioThresh) {
              continue;
            }
          }

          const bodyTop = o;
          const bodyBot = c;
          const bodyLen = Math.max(0.0001, bodyTop - bodyBot);
          const belowPct = Math.max(o, h) < hmaVal ? 1.0 : Math.max(0.0, Math.min(bodyTop, hmaVal) - bodyBot) / bodyLen;

          if (belowPct >= minBodyPct) {
            // Scan lookback window for candidate candles completely above HMA
            const wStart = Math.max(0, i - scanWindow);
            const candidateIndices = [];
            for (let k = wStart; k < i; k++) {
              if (lows[k] > hmaValues[k]) {
                candidateIndices.push(k);
              }
            }

            if (candidateIndices.length > 0) {
              let maxIdx = candidateIndices[0];
              for (let idx of candidateIndices) {
                if (highs[idx] > highs[maxIdx]) {
                  maxIdx = idx;
                }
              }

              // 1. Must be within recent bars (1 <= i - maxIdx <= maxBarsFromExtreme)
              // 2. Must not have been traded already
              if (i - maxIdx >= 1 && i - maxIdx <= maxBarsFromExtreme) {
                const maxH = highs[maxIdx];
                const maxL = lows[maxIdx];
                const maxT = times[maxIdx];

                if (!usedPeakTimes.has(maxT)) {
                  // Standard clean breakdown below base high's low
                  if (prevC >= maxL && c <= maxL) {
                    const marketEntry = Math.round(c * factor) / factor;
                    const slPrice = Math.round((maxH + slBufferPrice) * factor) / factor;
                    const marketRisk = slPrice - marketEntry;
                    const marketRiskPips = Math.round(marketRisk * pipMultiplier * 10) / 10;

                    if (marketRiskPips >= minRiskPips && marketRiskPips <= maxRiskPips) {
                      let entryMode = "MARKET";
                      let entryPrice = marketEntry;

                      // Dynamic Limit on large breakout candle (Pullback 50% of entire SL Zone)
                      if (dynamicLimitEnable && marketRiskPips > limitThresholdPips) {
                        entryMode = "LIMIT";
                        const pct = limitPullbackPct / 100.0;
                        entryPrice = Math.round((c + pct * (slPrice - c)) * factor) / factor;
                      }

                      const risk = slPrice - entryPrice;
                      const riskPips = Math.round(risk * pipMultiplier * 10) / 10;
                      const tp1Price = Math.round((marketEntry - r1 * marketRisk) * factor) / factor;
                      const tp2Price = Math.round((marketEntry - r2 * marketRisk) * factor) / factor;

                      const baseBox = {
                        x1: maxT,
                        x2: t,
                        y1: maxL,
                        y2: maxH,
                        type: "SELL_BASE",
                        label: "Base High",
                      };
                      baseBoxes.push(baseBox);

                      const reasons = [
                        `Bắt sóng hồi từ đỉnh cao nhất ${scanWindow} nến (${i - maxIdx} nến trước)`,
                        `Nến giảm đóng cửa có ${Math.round(belowPct * 100)}% thân nằm dưới HMA`,
                        `Phá vỡ tức thời đáy cây nến đỉnh cao nhất (${maxL})`,
                        `Cắt lỗ trên đỉnh râu nến (${maxH})`
                      ];

                      if (entryMode === "LIMIT") {
                        reasons.unshift(`SL nến lớn (${marketRiskPips}p > ${limitThresholdPips}p): Chờ Limit hồi ${Math.round(limitPullbackPct)}% Zone tại ${entryPrice} (Hủy sau ${limitCancelBars} nến)`);
                      }

                      const sig = {
                        id: `hma_${t}_SELL`,
                        index: i,
                        time: t,
                        action: "SELL",
                        type: "HMA BREAKOUT SELL",
                        entry_mode: entryMode,
                        market_entry: marketEntry,
                        price: entryPrice,
                        entry: entryPrice,
                        order_price: entryPrice,
                        sl: slPrice,
                        tp1: tp1Price,
                        tp2: tp2Price,
                        tp: tp1Price,
                        r1,
                        r2,
                        rr_ratio: `1:${r1}`,
                        confidence: 92,
                        reasons,
                        riskPips,
                        risk_pips: riskPips,
                        market_risk_pips: marketRiskPips,
                        limit_cancel_bars: limitCancelBars,
                        limit_pullback_pct: limitPullbackPct,
                        baseBox,
                        base_box: baseBox,
                        status: "OPEN",
                        exitTime: null,
                        exitPrice: null,
                        pnl_diff: 0.0,
                        pnlPips: 0,
                        win: false,
                      };

                      rawTrades.push(sig);
                      usedPeakTimes.add(maxT);
                      lastSignalIdx = i;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  // ================= 3. BACKTEST SIMULATION (BAR-BY-BAR) =================
  const completedTrades = [];
  const chartMarkers = [];
  const sigByBar = new Map();
  rawTrades.forEach((s) => sigByBar.set(s.index, s));

  for (let sIdx = 0; sIdx < rawTrades.length; sIdx++) {
    const tr = rawTrades[sIdx];
    const isBuy = tr.action === "BUY";
    const isLimit = tr.entry_mode === "LIMIT";
    let filled = !isLimit;
    let fillIdx = tr.index;

    if (isLimit) {
      const maxK = Math.min(n, tr.index + 1 + limitCancelBars);
      for (let k = tr.index + 1; k < maxK; k++) {
        if (isBuy) {
          if (lows[k] <= tr.entry) {
            filled = true;
            fillIdx = k;
            tr.fill_time = times[k];
            tr.fillTime = times[k];
            tr.fill_idx = k;
            break;
          }
        } else {
          if (highs[k] >= tr.entry) {
            filled = true;
            fillIdx = k;
            tr.fill_time = times[k];
            tr.fillTime = times[k];
            tr.fill_idx = k;
            break;
          }
        }
      }

      if (!filled) {
        if ((n - 1 - tr.index) <= limitCancelBars) {
          tr.status = "PENDING_LIMIT";
          tr.exitTime = times[n - 1];
          tr.exit_time = times[n - 1];
          tr.pnl_diff = 0.0;
          tr.pnlPips = 0;
          tr.pnl_pips = 0;
          tr.win = false;
        } else {
          tr.status = "TIMEOUT";
          const exitT = times[Math.min(n - 1, tr.index + limitCancelBars)];
          tr.exitTime = exitT;
          tr.exit_time = exitT;
          tr.pnl_diff = 0.0;
          tr.pnlPips = 0;
          tr.pnl_pips = 0;
          tr.win = false;
        }

        completedTrades.push(tr);

        const markerText = isBuy ? "LIMIT BUY" : "LIMIT SELL";
        chartMarkers.push({
          time: tr.time,
          position: isBuy ? "belowBar" : "aboveBar",
          color: tr.status === "PENDING_LIMIT" ? "#f59e0b" : (isBuy ? "#10b981" : "#ef4444"),
          shape: isBuy ? "arrowUp" : "arrowDown",
          text: markerText,
          id: `hma_${tr.time}`,
          size: 1,
        });
        continue;
      }
    }

    // Active trade from fillIdx + 1 onwards
    let isFinished = false;
    let currentSl = tr.sl;
    const originalSl = tr.sl;
    let beTriggered = false;
    const tpDistance = Math.abs(tr.tp1 - tr.entry);
    const triggerDist = (dynamicBeEnable && tpDistance > 0) ? (beTriggerPct * tpDistance) : 999999.0;
    const nearTpDist = (nearTpCutEnable && tpDistance > 0) ? (nearTpThresholdPct * tpDistance) : 999999.0;
    let nearTpZoneReached = false;
    let peakCandleIdx = -1;
    let peakPrice = isBuy ? -1.0 : 999999.0;

    for (let j = fillIdx + 1; j < n; j++) {
      const bh = highs[j];
      const bl = lows[j];
      const bt = times[j];

      if (isBuy) {
        // 1. Check BE Trigger when price reaches X% TP
        if (!beTriggered && dynamicBeEnable) {
          if (bh >= (tr.entry + triggerDist)) {
            beTriggered = true;
            const profitAtTriggerPips = triggerDist * pipMultiplier;
            const tpPips = tpDistance * pipMultiplier;
            let actualLockPips = 5.0;
            if (beLockPips > 0 && beLockPips <= (profitAtTriggerPips - 2.0) && beLockPips < tpPips) {
              actualLockPips = beLockPips;
            } else {
              actualLockPips = Math.min(5.0, Math.max(0.0, profitAtTriggerPips * 0.5));
            }
            const actualLockPrice = actualLockPips / pipMultiplier;
            currentSl = Math.max(currentSl, tr.entry + actualLockPrice);
          }
        }

        // 2. Check Near-TP Zone & Reversal Exit
        if (nearTpCutEnable) {
          if (bh >= (tr.entry + nearTpDist)) {
            nearTpZoneReached = true;
            if (bh > peakPrice) {
              peakPrice = bh;
              peakCandleIdx = j;
            }
          }
          if (nearTpZoneReached && peakCandleIdx >= 0 && j > peakCandleIdx) {
            const refLow = lows[peakCandleIdx];
            if (closes[j] < refLow) {
              tr.status = "WIN_EARLY_CUT";
              tr.exitTime = bt;
              tr.exit_time = bt;
              tr.exitPrice = closes[j];
              tr.exit_price = closes[j];
              tr.pnl_diff = Math.round((closes[j] - tr.entry) * 1000) / 1000;
              tr.pnlPips = Math.round((closes[j] - tr.entry) * pipMultiplier * 10) / 10;
              tr.pnl_pips = tr.pnlPips;
              tr.win = tr.pnlPips > 0;
              isFinished = true;
              break;
            }
          }
        }

        // 3. Check TP Hit
        if (bh >= tr.tp1) {
          tr.status = "WIN_TP";
          tr.exitTime = bt;
          tr.exit_time = bt;
          tr.exitPrice = tr.tp1;
          tr.exit_price = tr.tp1;
          tr.pnl_diff = Math.round((tr.tp1 - tr.entry) * 1000) / 1000;
          tr.pnlPips = Math.round((tr.tp1 - tr.entry) * pipMultiplier * 10) / 10;
          tr.pnl_pips = tr.pnlPips;
          tr.win = true;
          isFinished = true;
          break;
        }

        // 3. Check SL / BE Hit
        if (bl <= currentSl) {
          if (beTriggered && currentSl > originalSl) {
            tr.status = "BE_WIN";
            tr.exitTime = bt;
            tr.exit_time = bt;
            tr.exitPrice = currentSl;
            tr.exit_price = currentSl;
            tr.pnl_diff = Math.round((currentSl - tr.entry) * 1000) / 1000;
            tr.pnlPips = Math.round(tr.pnl_diff * pipMultiplier * 10) / 10;
            tr.pnl_pips = tr.pnlPips;
            tr.win = true;
          } else {
            tr.status = "LOSS_SL";
            tr.exitTime = bt;
            tr.exit_time = bt;
            tr.exitPrice = originalSl;
            tr.exit_price = originalSl;
            tr.pnl_diff = Math.round((originalSl - tr.entry) * 1000) / 1000;
            tr.pnlPips = Math.round((originalSl - tr.entry) * pipMultiplier * 10) / 10;
            tr.pnl_pips = tr.pnlPips;
            tr.win = false;
          }
          isFinished = true;
          break;
        }
      } else {
        // 1. Check BE Trigger when price reaches X% TP
        if (!beTriggered && dynamicBeEnable) {
          if (bl <= (tr.entry - triggerDist)) {
            beTriggered = true;
            const profitAtTriggerPips = triggerDist * pipMultiplier;
            const tpPips = tpDistance * pipMultiplier;
            let actualLockPips = 5.0;
            if (beLockPips > 0 && beLockPips <= (profitAtTriggerPips - 2.0) && beLockPips < tpPips) {
              actualLockPips = beLockPips;
            } else {
              actualLockPips = Math.min(5.0, Math.max(0.0, profitAtTriggerPips * 0.5));
            }
            const actualLockPrice = actualLockPips / pipMultiplier;
            currentSl = Math.min(currentSl, tr.entry - actualLockPrice);
          }
        }

        // 2. Check Near-TP Zone & Reversal Exit
        if (nearTpCutEnable) {
          if (bl <= (tr.entry - nearTpDist)) {
            nearTpZoneReached = true;
            if (bl < peakPrice) {
              peakPrice = bl;
              peakCandleIdx = j;
            }
          }
          if (nearTpZoneReached && peakCandleIdx >= 0 && j > peakCandleIdx) {
            const refHigh = highs[peakCandleIdx];
            if (closes[j] > refHigh) {
              tr.status = "WIN_EARLY_CUT";
              tr.exitTime = bt;
              tr.exit_time = bt;
              tr.exitPrice = closes[j];
              tr.exit_price = closes[j];
              tr.pnl_diff = Math.round((tr.entry - closes[j]) * 1000) / 1000;
              tr.pnlPips = Math.round((tr.entry - closes[j]) * pipMultiplier * 10) / 10;
              tr.pnl_pips = tr.pnlPips;
              tr.win = tr.pnlPips > 0;
              isFinished = true;
              break;
            }
          }
        }

        // 3. Check TP Hit
        if (bl <= tr.tp1) {
          tr.status = "WIN_TP";
          tr.exitTime = bt;
          tr.exit_time = bt;
          tr.exitPrice = tr.tp1;
          tr.exit_price = tr.tp1;
          tr.pnl_diff = Math.round((tr.entry - tr.tp1) * 1000) / 1000;
          tr.pnlPips = Math.round((tr.entry - tr.tp1) * pipMultiplier * 10) / 10;
          tr.pnl_pips = tr.pnlPips;
          tr.win = true;
          isFinished = true;
          break;
        }

        // 3. Check SL / BE Hit
        if (bh >= currentSl) {
          if (beTriggered && currentSl < originalSl) {
            tr.status = "BE_WIN";
            tr.exitTime = bt;
            tr.exit_time = bt;
            tr.exitPrice = currentSl;
            tr.exit_price = currentSl;
            tr.pnl_diff = Math.round((tr.entry - currentSl) * 1000) / 1000;
            tr.pnlPips = Math.round(tr.pnl_diff * pipMultiplier * 10) / 10;
            tr.pnl_pips = tr.pnlPips;
            tr.win = true;
          } else {
            tr.status = "LOSS_SL";
            tr.exitTime = bt;
            tr.exit_time = bt;
            tr.exitPrice = originalSl;
            tr.exit_price = originalSl;
            tr.pnl_diff = Math.round((tr.entry - originalSl) * 1000) / 1000;
            tr.pnlPips = Math.round((tr.entry - originalSl) * pipMultiplier * 10) / 10;
            tr.pnl_pips = tr.pnlPips;
            tr.win = false;
          }
          isFinished = true;
          break;
        }
      }

      // 4. Check Opposite Signal Exit (Close on Opposite)
      if (closeOnOpposite && sigByBar.has(j)) {
        const oppSig = sigByBar.get(j);
        if (oppSig.action !== tr.action) {
          tr.status = "CLOSED_OPPOSITE";
          tr.exitTime = bt;
          tr.exit_time = bt;
          tr.exitPrice = closes[j];
          tr.exit_price = closes[j];
          tr.pnl_diff = Math.round((isBuy ? (closes[j] - tr.entry) : (tr.entry - closes[j])) * 1000) / 1000;
          tr.pnlPips = Math.round(tr.pnl_diff * pipMultiplier * 10) / 10;
          tr.pnl_pips = tr.pnlPips;
          tr.win = tr.pnlPips > 0;
          isFinished = true;
          break;
        }
      }
    }

    if (!isFinished) {
      tr.status = "RUNNING";
      tr.exitTime = times[n - 1];
      tr.exit_time = times[n - 1];
      const currP = closes[n - 1];
      tr.pnl_diff = Math.round((isBuy ? (currP - tr.entry) : (tr.entry - currP)) * 1000) / 1000;
      tr.pnlPips = Math.round(tr.pnl_diff * pipMultiplier * 10) / 10;
      tr.pnl_pips = tr.pnlPips;
      tr.win = tr.pnlPips > 0;
    }

    completedTrades.push(tr);

    // Chart markers (Clean Buy/Sell indicators)
    const markerText = (isLimit ? (isBuy ? "LIMIT BUY" : "LIMIT SELL") : (isBuy ? "BUY" : "SELL"));
    chartMarkers.push({
      time: tr.time,
      position: isBuy ? "belowBar" : "aboveBar",
      color: isBuy ? "#10b981" : "#ef4444",
      shape: isBuy ? "arrowUp" : "arrowDown",
      text: markerText,
      id: `hma_${tr.time}`,
      size: 1,
    });
  }

  // ================= 4. GENERATE VISUAL LINES & BASE BOX (LATEST TRADE SETUP ONLY) =================
  const latestSignal = completedTrades.length > 0 ? completedTrades[completedTrades.length - 1] : null;
  const visualLines = [];
  const displayBoxes = [];

  if (latestSignal) {
    const endT = times[n - 1];

    if (latestSignal.baseBox || latestSignal.base_box) {
      displayBoxes.push(latestSignal.baseBox || latestSignal.base_box);
    }

    const isLimit = latestSignal.entry_mode === "LIMIT";
    const isPending = latestSignal.status === "PENDING_LIMIT";
    const entryColor = isPending ? "#f59e0b" : "#38bdf8";
    const entryStyle = isPending ? 2 : 0;
    const entryLabel = isPending ? `LIMIT ENTRY: ${latestSignal.entry} (Wait Retest)` : `ENTRY: ${latestSignal.entry}`;

    visualLines.push({
      x1: latestSignal.time,
      x2: endT,
      y1: latestSignal.entry,
      y2: latestSignal.entry,
      color: entryColor,
      width: 1.5,
      style: entryStyle,
      label: entryLabel,
    });

    visualLines.push({
      x1: latestSignal.time,
      x2: endT,
      y1: latestSignal.sl,
      y2: latestSignal.sl,
      color: "#ef4444",
      width: 1.5,
      style: 2, // Dashed SL
      label: `SL: ${latestSignal.sl} (-${latestSignal.riskPips || latestSignal.risk_pips}p)`,
    });

    visualLines.push({
      x1: latestSignal.time,
      x2: endT,
      y1: latestSignal.tp1,
      y2: latestSignal.tp1,
      color: "#10b981",
      width: 1.5,
      style: 2, // Dashed TP
      label: `TP (RR 1:${r1}): ${latestSignal.tp1}`,
    });
  }

  // ================= 5. COMPUTE BACKTEST SUMMARY & WINDOWS =================
  const computeStats = (trList) => {
    const finishedTrades = trList.filter((t) => t.status === "WIN_TP" || t.status === "LOSS_SL" || t.status === "BE_WIN" || t.status === "CLOSED_OPPOSITE" || t.status === "WIN_EARLY_CUT");
    const total = finishedTrades.length;
    if (total === 0) {
      return {
        total_trades: 0,
        win_rate: 0,
        wins: 0,
        losses: 0,
        net_pnl: 0,
        profit_factor: 1.0,
        avg_rr: `1:${r1}`,
        max_drawdown_pips: 0,
        trades: [],
        max_win_streak: 0,
        max_loss_streak: 0,
        streaks: { max_win_streak: 0, max_loss_streak: 0 }
      };
    }

    const wins = finishedTrades.filter((t) => t.win).length;
    const losses = finishedTrades.filter((t) => !t.win).length;
    const winRate = total > 0 ? Math.round((wins / total) * 1000) / 10 : 0;
    const netPnl = Math.round(finishedTrades.reduce((sum, t) => sum + (t.pnlPips ?? t.pnl_pips ?? 0), 0) * 10) / 10;

    const grossProfit = finishedTrades.filter((t) => (t.pnlPips ?? t.pnl_pips ?? 0) > 0).reduce((sum, t) => sum + (t.pnlPips ?? t.pnl_pips ?? 0), 0);
    const grossLoss = Math.abs(finishedTrades.filter((t) => (t.pnlPips ?? t.pnl_pips ?? 0) < 0).reduce((sum, t) => sum + (t.pnlPips ?? t.pnl_pips ?? 0), 0));
    const profitFactor = grossLoss > 0 ? Math.round((grossProfit / grossLoss) * 100) / 100 : (grossProfit > 0 ? 99.9 : 1.0);

    let wMaxWin = 0;
    let wMaxLoss = 0;
    let wCurWin = 0;
    let wCurLoss = 0;
    finishedTrades.forEach((t) => {
      if (t.win) {
        wCurWin++;
        wCurLoss = 0;
        if (wCurWin > wMaxWin) wMaxWin = wCurWin;
      } else {
        wCurLoss++;
        wCurWin = 0;
        if (wCurLoss > wMaxLoss) wMaxLoss = wCurLoss;
      }
    });

    return {
      total_trades: total,
      win_rate: winRate,
      wins,
      losses,
      net_pnl: netPnl,
      net_pnl_pips: netPnl,
      profit_factor: profitFactor,
      avg_rr: `1:${r1}`,
      max_drawdown_pips: 0,
      trades: finishedTrades,
      max_win_streak: wMaxWin,
      max_loss_streak: wMaxLoss,
      streaks: { max_win_streak: wMaxWin, max_loss_streak: wMaxLoss }
    };
  };

  // Streaks calculation
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let curWin = 0;
  let curLoss = 0;
  completedTrades.forEach((t) => {
    if (t.win) {
      curWin++;
      curLoss = 0;
      if (curWin > maxWinStreak) maxWinStreak = curWin;
    } else {
      curLoss++;
      curWin = 0;
      if (curLoss > maxLossStreak) maxLossStreak = curLoss;
    }
  });

  // Calendar day window calculation (ICT / UTC+7) anchored to Real-World Calendar Day
  const nowMs = Date.now();
  const nowVnDate = new Date(nowMs + 7 * 3600 * 1000);
  const vnYear = nowVnDate.getUTCFullYear();
  const vnMonth = nowVnDate.getUTCMonth();
  const vnDay = nowVnDate.getUTCDate();
  const todayStartTs = Math.floor(Date.UTC(vnYear, vnMonth, vnDay, 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const tradesToday = completedTrades.filter((t) => t.time >= todayStartTs);
  const trades3d = completedTrades.filter((t) => t.time >= threeDaysStartTs);
  const trades7d = completedTrades.filter((t) => t.time >= sevenDaysStartTs);
  const trades30d = completedTrades.filter((t) => t.time >= thirtyDaysStartTs);

  const backtestWindows = {
    today: computeStats(tradesToday),
    "24h": computeStats(tradesToday),
    "3d": computeStats(trades3d),
    "7d": computeStats(trades7d),
    "30d": computeStats(trades30d),
    all: computeStats(completedTrades),
  };

  // Market Bias
  let marketBias = "NEUTRAL";
  if (hmaLine.length >= 5) {
    const lastHma = hmaLine[hmaLine.length - 1];
    const prevHma = hmaLine[hmaLine.length - 4];
    if (lastHma.isBull && lastHma.value > prevHma.value) {
      marketBias = "BULLISH";
    } else if (!lastHma.isBull && lastHma.value < prevHma.value) {
      marketBias = "BEARISH";
    }
  }

  return {
    hma: hmaLine,
    signals: completedTrades,
    recent_signals: completedTrades,
    all_signals: completedTrades,
    latest_signal: latestSignal,
    latestSignal,
    chart_markers: chartMarkers,
    chartMarkers,
    visual_lines: visualLines,
    visualLines,
    base_boxes: displayBoxes,
    baseBoxes: displayBoxes,
    ema200_sw_filter_enable: activeEma200SwFilter,
    ema200_sw_max_dist_pips: ema200SwMaxDistPips,
    adaptive_period_enable: adaptivePeriodEnable,
    adaptive_min_period: adaptiveMinPeriod,
    adaptive_max_period: adaptiveMaxPeriod,
    hma_adaptive_period_enable: adaptivePeriodEnable,
    hma_adaptive_min_period: adaptiveMinPeriod,
    hma_adaptive_max_period: adaptiveMaxPeriod,
    market_bias: marketBias,
    streaks: { max_win_streak: maxWinStreak, max_loss_streak: maxLossStreak },
    backtest: backtestWindows.all,
    backtest_windows: backtestWindows,
  };
}
